/**
 * @file ImageFrameFields.tsx
 * @description Режим и кадр фото в колонке (phase19.16, партия SB120,
 * этап 1; uiux-sb120-stage1 §3 и «Дополнение» §3; данные
 * db-design-sb120-stage1 §1 `mode`, `focus`/`focusPhone`,
 * `aspect`/`aspectPhone`):
 *
 * ```
 * Режим  [Обычное][Вырезанный объект]
 * Кадр   [ ПК | Телефон ]   ⌖ точка фокуса · пропорции 0,2–6 ×
 *        Телефон: ↺ как на ПК
 * ```
 *
 * Пропорции: нет ключа — как у файла (фото целиком). При высоте «По фото»
 * без своих пропорций поле свёрнуто до строки «как у файла · Свой кадр ›».
 */

import type { SectionElementOf } from "@chaiberi/shared";
import { RotateCcw } from "lucide-react";
import { useState } from "react";

import { Button } from "../../../ui/button";
import { Label } from "../../../ui/label";
import { Segmented } from "../../../site-view/Segmented";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { DeviceTabs } from "../fields/DeviceTabs";
import { FocalPointPicker } from "../fields/FocalPointPicker";
import { useSectionForm } from "../section-form-context";
import type { Device } from "../../../../lib/site-builder/look-size";

type Image = SectionElementOf<"image">;

const MODE = [
  { value: "fit" as const, label: "Обычное" },
  { value: "bleed" as const, label: "Вырезанный объект" },
];

/** Пропорции кадра — подсказки (ширина ÷ высота). */
const ASPECT_PRESETS = [
  { label: "1:1", value: 1 },
  { label: "4:3", value: 1.333 },
  { label: "3:4", value: 0.75 },
  { label: "16:9", value: 1.778 },
  { label: "3:2", value: 1.5 },
];

interface Props {
  element: Image;
  path: string;
  onChange: (element: Image) => void;
}

export function ImageFrameFields({ element, path, onChange }: Props) {
  const { preview } = useSectionForm();
  const [device, setDevice] = useState<Device>("desktop");
  const [expanded, setExpanded] = useState(false);
  const phone = device === "phone";
  const focusKey = phone ? "focusPhone" : "focus";
  const aspectKey = phone ? "aspectPhone" : "aspect";
  const focus = (phone ? element.focusPhone : undefined) ?? element.focus ?? { x: 50, y: 50 };
  const own = phone && (element.focusPhone !== undefined || element.aspectPhone !== undefined);
  const collapsed = !phone && element.height.kind === "photo" && element.aspect === undefined && !expanded;

  function unset(keys: ReadonlyArray<keyof Image>) {
    const next = { ...element };
    for (const key of keys) delete next[key];
    onChange(next);
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Label>Режим</Label>
      <Segmented aria-label="Режим фото" options={MODE} value={element.mode} onChange={(mode) => onChange({ ...element, mode })} />
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <Label>Кадр</Label>
        <DeviceTabs value={device} onChange={setDevice} label="Кадр фото: экран" phoneOwn={element.focusPhone !== undefined || element.aspectPhone !== undefined} />
      </div>
      {own ? (
        <Button variant="ghost" size="sm" className="h-12 self-start can-hover:h-8" onClick={() => unset(["focusPhone", "aspectPhone"])} aria-label="Кадр на телефоне: как на ПК">
          <RotateCcw aria-hidden />
          как на ПК
        </Button>
      ) : phone ? (
        <span className="type-caption text-on-surface-variant">Сейчас как на ПК.</span>
      ) : null}
      {element.mediaId ? <FocalPointPicker mediaId={element.mediaId} value={focus} alt={element.alt} onChange={(next) => onChange({ ...element, [focusKey]: next })} /> : null}
      {collapsed ? (
        <p className="type-caption flex flex-wrap items-center gap-2 text-on-surface-variant">
          Пропорции: как у файла — фото целиком.
          <button type="button" className="inline-flex h-12 items-center rounded-lg px-2 text-sm font-medium text-primary hover:bg-surface-low can-hover:h-8" onClick={() => setExpanded(true)}>
            Свой кадр ›
          </button>
        </p>
      ) : (
        <NumberSliderField
          label={phone ? "Пропорции на телефоне" : "Пропорции"}
          value={element[aspectKey]}
          inherit={phone ? { value: element.aspect, label: "как на ПК" } : { label: "как у файла" }}
          min={0.2}
          max={6}
          step={0.01}
          unit="×"
          display={{ factor: 1, decimals: 2 }}
          presets={ASPECT_PRESETS}
          hint="Ширина ÷ высота: 1 — квадрат, больше 1 — шире, меньше 1 — выше."
          onChange={(v) => (v === undefined ? unset([aspectKey]) : onChange({ ...element, [aspectKey]: v }))}
          onPreview={(v) => preview(`${path}.${aspectKey}`, v)}
        />
      )}
    </div>
  );
}
